/**
 * Kukutis hero slider (sections/kk-hero-slider.liquid).
 * The track is a horizontal scroll-snap strip, so swipe works natively; arrows, dots and the arrow keys
 * scroll it one slide at a time. Hidden slides are made inert so their links are not tabbable.
 * Autoplay (data-autoplay seconds, 0 = off) keeps turning the slides; a manual change (arrow, dot, swipe,
 * key) restarts the interval. It waits while keyboard focus is inside the slider or the tab is hidden,
 * and does not run when the visitor's system asks for reduced motion.
 */
const REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)');

class KkHeroSlider extends HTMLElement {
  #index = 0;
  #timer = 0;
  #hold = false;
  #lock = 0;

  connectedCallback() {
    this.track = this.querySelector('[data-kk-track]');
    this.slides = [...this.querySelectorAll('[data-kk-slide]')];
    this.dots = [...this.querySelectorAll('[data-kk-dot]')];
    if (!this.track || this.slides.length < 2) return;

    this.querySelector('[data-kk-prev]')?.addEventListener('click', () => this.go(this.#index - 1, true));
    this.querySelector('[data-kk-next]')?.addEventListener('click', () => this.go(this.#index + 1, true));
    this.dots.forEach((dot) => dot.addEventListener('click', () => this.go(Number(dot.dataset.kkDot), true)));
    this.addEventListener('keydown', this.#onKeydown);

    // Follow manual swipes: the slide closest to the left edge is the current one.
    this.track.addEventListener('scroll', () => {
      window.cancelAnimationFrame(this.raf);
      this.raf = window.requestAnimationFrame(() => {
        // Ignore the in-between positions of an arrow / dot / autoplay scroll.
        if (Date.now() < this.#lock) return;
        const index = Math.round(this.track.scrollLeft / this.track.clientWidth);
        if (index !== this.#index) this.#setCurrent(index);
      });
    });
    this.track.addEventListener('touchstart', () => this.#restart(), { passive: true });

    // Wait only for keyboard focus (a mouse click on an arrow also focuses it).
    this.addEventListener('focusin', (event) => (this.#hold = event.target instanceof Element && event.target.matches(':focus-visible')));
    this.addEventListener('focusout', () => (this.#hold = false));

    this.#setCurrent(0);
    this.#startAutoplay();
  }

  disconnectedCallback() {
    window.clearInterval(this.#timer);
  }

  /**
   * @param {number} index
   * @param {boolean} [manual]
   */
  go(index, manual = false) {
    const count = this.slides.length;
    const next = ((index % count) + count) % count;
    if (manual) this.#restart();
    this.#lock = Date.now() + 700;
    this.track.scrollTo({ left: next * this.track.clientWidth, behavior: REDUCED.matches ? 'auto' : 'smooth' });
    this.#setCurrent(next);
  }

  /** @param {number} index */
  #setCurrent(index) {
    this.#index = index;
    this.slides.forEach((slide, i) => {
      const current = i === index;
      slide.toggleAttribute('inert', !current);
      slide.setAttribute('aria-hidden', String(!current));
    });
    this.dots.forEach((dot, i) => {
      if (i === index) dot.setAttribute('aria-current', 'true');
      else dot.removeAttribute('aria-current');
    });
  }

  /** @param {KeyboardEvent} event */
  #onKeydown = (event) => {
    if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.go(this.#index + 1, true);
    } else if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.go(this.#index - 1, true);
    }
  };

  #startAutoplay() {
    this.seconds = Number(this.dataset.autoplay || 0);
    if (!this.seconds || REDUCED.matches) return;
    this.#restart();
  }

  /** (Re)starts the timer, so after a manual change the next slide comes a full interval later. */
  #restart() {
    if (!this.seconds || REDUCED.matches) return;
    window.clearInterval(this.#timer);
    this.#timer = window.setInterval(() => {
      if (this.#hold || document.hidden) return;
      this.go(this.#index + 1);
    }, this.seconds * 1000);
  }
}

if (!customElements.get('kk-hero-slider')) customElements.define('kk-hero-slider', KkHeroSlider);
